import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  Calendar,
  Download,
  FileText,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserX,
  Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'energie', label: 'Energie' },
  { value: 'internet_telefonie', label: 'Internet / telefonie' },
  { value: 'ccli_licentie', label: 'CCLI-licentie' },
  { value: 'verhuur', label: 'Verhuurcontract' },
  { value: 'onderhoud', label: 'Onderhoudscontract' },
  { value: 'software', label: 'Softwarelicentie' },
  { value: 'verzekering', label: 'Verzekering' },
  { value: 'schoonmaak', label: 'Schoonmaakcontract' },
  { value: 'lease_apparatuur', label: 'Lease / apparatuur' },
  { value: 'abonnement', label: 'Abonnement' },
  { value: 'overig', label: 'Overig' },
];

const STATUSES = [
  { value: 'concept', label: 'Concept', className: 'bg-slate-100 text-slate-700' },
  { value: 'actief', label: 'Actief', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'verloopt_binnenkort', label: 'Verloopt binnenkort', className: 'bg-amber-100 text-amber-700' },
  { value: 'verlopen', label: 'Verlopen', className: 'bg-red-100 text-red-700' },
  { value: 'opgezegd', label: 'Opgezegd', className: 'bg-slate-100 text-slate-500' },
];

const FREQUENCIES = [
  { value: 'maandelijks', label: 'Maandelijks' },
  { value: 'kwartaal', label: 'Per kwartaal' },
  { value: 'halfjaarlijks', label: 'Halfjaarlijks' },
  { value: 'jaarlijks', label: 'Jaarlijks' },
  { value: 'eenmalig', label: 'Eenmalig' },
];

const DEPARTMENTS = [
  { value: 'facilitair', label: 'Facilitair' },
  { value: 'worship', label: 'Worship' },
  { value: 'beheer', label: 'Beheer' },
  { value: 'connectgroepen', label: 'Connectgroepen' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'overig', label: 'Overig' },
];

const EMPTY_FORM = {
  name: '',
  relation_id: '',
  category: 'overig',
  contract_number: '',
  start_date: '',
  end_date: '',
  notice_period_days: '',
  auto_renewal: false,
  cost_amount: '',
  cost_frequency: 'maandelijks',
  responsible_user_id: '',
  department: '',
  notes: '',
  status: 'actief',
};

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value || '-';
const getClassName = (options, value) => options.find((option) => option.value === value)?.className || 'bg-slate-100 text-slate-700';
const formatCurrency = (value) => `€ ${Number(value || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ContractManagementPage = () => {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('contracts_manage');

  const [contracts, setContracts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [relations, setRelations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [contractsRes, dashboardRes, relationsRes, teamRes] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/contracts/dashboard`),
        axios.get(`${API}/relations`),
        axios.get(`${API}/team`),
      ]);
      setContracts(contractsRes.data);
      setDashboard(dashboardRes.data);
      setRelations(relationsRes.data);
      setTeamMembers(teamRes.data);
    } catch (error) {
      toast.error('Kon contractbeheer niet laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`);
      setContracts(response.data);
      const dashboardRes = await axios.get(`${API}/contracts/dashboard`);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Kon contracten niet laden');
    }
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      const statusMatches = statusFilter === 'all' || contract.status === statusFilter;
      const categoryMatches = categoryFilter === 'all' || contract.category === categoryFilter;
      const departmentMatches = departmentFilter === 'all' || contract.department === departmentFilter;
      return statusMatches && categoryMatches && departmentMatches;
    });
  }, [contracts, statusFilter, categoryFilter, departmentFilter]);

  const resetForm = () => {
    setEditingContract(null);
    setFormData(EMPTY_FORM);
    setDocuments([]);
  };

  const normalizePayload = () => ({
    ...formData,
    relation_id: formData.relation_id || null,
    contract_number: formData.contract_number || null,
    start_date: formData.start_date || null,
    end_date: formData.end_date || null,
    notice_period_days: formData.notice_period_days ? parseInt(formData.notice_period_days, 10) : null,
    cost_amount: formData.cost_amount ? parseFloat(formData.cost_amount) : null,
    responsible_user_id: formData.responsible_user_id || null,
    department: formData.department || null,
    notes: formData.notes || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name) {
      toast.error('Contractnaam is verplicht');
      return;
    }
    try {
      const payload = normalizePayload();
      if (editingContract) {
        await axios.put(`${API}/contracts/${editingContract.id}`, payload);
        toast.success('Contract bijgewerkt');
      } else {
        await axios.post(`${API}/contracts`, payload);
        toast.success('Contract toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit contract wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/contracts/${id}`);
      toast.success('Contract verwijderd');
      fetchContracts();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const fetchDocuments = async (contractId) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Kon documenten niet laden');
    }
  };

  const openEditDialog = (contract) => {
    setEditingContract(contract);
    setFormData({
      name: contract.name || '',
      relation_id: contract.relation_id || '',
      category: contract.category || 'overig',
      contract_number: contract.contract_number || '',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      notice_period_days: contract.notice_period_days != null ? String(contract.notice_period_days) : '',
      auto_renewal: Boolean(contract.auto_renewal),
      cost_amount: contract.cost_amount != null ? String(contract.cost_amount) : '',
      cost_frequency: contract.cost_frequency || 'maandelijks',
      responsible_user_id: contract.responsible_user_id || '',
      department: contract.department || '',
      notes: contract.notes || '',
      status: contract.status || 'actief',
    });
    setDocuments([]);
    fetchDocuments(contract.id);
    setDialogOpen(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editingContract) return;
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('file', file);
      await axios.post(`${API}/contracts/${editingContract.id}/documents`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document geüpload');
      fetchDocuments(editingContract.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload mislukt');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadDocument = async (documentItem) => {
    try {
      const response = await axios.get(
        `${API}/contracts/${editingContract.id}/documents/${documentItem.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentItem.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Downloaden mislukt');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Dit document verwijderen?')) return;
    try {
      await axios.delete(`${API}/contracts/${editingContract.id}/documents/${documentId}`);
      toast.success('Document verwijderd');
      fetchDocuments(editingContract.id);
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openSettingsDialog = async () => {
    try {
      const response = await axios.get(`${API}/contracts/notification-settings`);
      setSettingsForm({
        ...response.data,
        days_before_end: response.data.days_before_end.join(', '),
        days_before_notice_deadline: response.data.days_before_notice_deadline.join(', '),
        days_before_auto_renewal: response.data.days_before_auto_renewal.join(', '),
      });
      setSettingsOpen(true);
    } catch (error) {
      toast.error('Kon meldingsinstellingen niet laden');
    }
  };

  const parseDaysList = (value) =>
    value.split(',').map((item) => parseInt(item.trim(), 10)).filter((n) => !Number.isNaN(n));

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    try {
      await axios.put(`${API}/contracts/notification-settings`, {
        ...settingsForm,
        days_before_end: parseDaysList(settingsForm.days_before_end),
        days_before_notice_deadline: parseDaysList(settingsForm.days_before_notice_deadline),
        days_before_auto_renewal: parseDaysList(settingsForm.days_before_auto_renewal),
      });
      toast.success('Meldingsinstellingen opgeslagen');
      setSettingsOpen(false);
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };

  const toggleRecipientRole = (role) => {
    setSettingsForm((prev) => {
      const roles = prev.recipient_roles.includes(role)
        ? prev.recipient_roles.filter((r) => r !== role)
        : [...prev.recipient_roles, role];
      return { ...prev, recipient_roles: roles };
    });
  };

  const toggleRecipientUser = (userId) => {
    setSettingsForm((prev) => {
      const ids = prev.recipient_user_ids.includes(userId)
        ? prev.recipient_user_ids.filter((id) => id !== userId)
        : [...prev.recipient_user_ids, userId];
      return { ...prev, recipient_user_ids: ids };
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-56"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-24 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contract-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Contractbeheer</h1>
            <p className="text-slate-500">Alle contracten van je organisatie op één plek, met tijdige meldingen</p>
          </div>
          {canManage && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={openSettingsDialog} className="rounded-xl w-full sm:w-auto">
                <Bell className="w-4 h-4 mr-2" />
                Meldingen instellen
              </Button>
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Contract toevoegen
              </Button>
            </div>
          )}
        </div>

        {dashboard && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Actieve contracten</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard.active_count}</p>
                </div>
                <FileText className="w-8 h-8 text-teal-600" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Verloopt binnenkort</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard.expiring_soon_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Verlopen</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard.expired_count}</p>
                </div>
                <Calendar className="w-8 h-8 text-red-500" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Zonder verantwoordelijke</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard.without_responsible_count}</p>
                </div>
                <UserX className="w-8 h-8 text-orange-500" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm sm:col-span-2">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Totale kosten per maand</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard.total_monthly_cost)}</p>
                </div>
                <Wallet className="w-8 h-8 text-indigo-500" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm sm:col-span-2">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Totale kosten per jaar</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard.total_yearly_cost)}</p>
                </div>
                <Wallet className="w-8 h-8 text-indigo-300" />
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Filter op status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  {STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Filter op categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle categorieën</SelectItem>
                  {CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Filter op afdeling" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle afdelingen</SelectItem>
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {filteredContracts.length > 0 ? (
          <div className="grid xl:grid-cols-2 gap-4">
            {filteredContracts.map((contract) => (
              <Card key={contract.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{contract.name}</h3>
                        <p className="text-sm text-slate-500 truncate">{contract.relation_name || 'Geen leverancier gekoppeld'}</p>
                        {contract.contract_number && (
                          <p className="text-xs text-slate-400 mt-1">Nr. {contract.contract_number}</p>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(contract)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Bewerken
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(contract.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(STATUSES, contract.status)}`}>
                      {getLabel(STATUSES, contract.status)}
                    </span>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                      {getLabel(CATEGORIES, contract.category)}
                    </span>
                    {contract.auto_renewal && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                        Automatische verlenging
                      </span>
                    )}
                    {!contract.responsible_user_id && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-50 text-orange-700">
                        Geen verantwoordelijke
                      </span>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm text-slate-600">
                    <div>
                      <p className="text-xs text-slate-400">Einddatum</p>
                      <p>{contract.end_date || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Kosten</p>
                      <p>{contract.cost_amount != null ? `${formatCurrency(contract.cost_amount)} (${getLabel(FREQUENCIES, contract.cost_frequency).toLowerCase()})` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Verantwoordelijke</p>
                      <p>{contract.responsible_user_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Afdeling</p>
                      <p>{getLabel(DEPARTMENTS, contract.department)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Geen contracten</h3>
              <p className="text-slate-500 mb-4">Leg energie-, huur-, onderhouds- en andere contracten centraal vast.</p>
              {canManage && (
                <Button
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Eerste contract toevoegen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContract ? 'Contract bewerken' : 'Contract toevoegen'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Contractnaam *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Bijv. Energiecontract kerkgebouw"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contract_number">Contractnummer</Label>
                  <Input
                    id="contract_number"
                    value={formData.contract_number}
                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                    placeholder="Bijv. EN-2026-0091"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Leverancier / relatie</Label>
                  <Select value={formData.relation_id || 'none'} onValueChange={(v) => setFormData({ ...formData, relation_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen relatie gekoppeld" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen relatie gekoppeld</SelectItem>
                      {relations.map((relation) => (
                        <SelectItem key={relation.id} value={relation.id}>{relation.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Categorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((category) => (
                        <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Startdatum</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Einddatum</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notice_period_days">Opzegtermijn (dagen)</Label>
                  <Input
                    id="notice_period_days"
                    type="number"
                    min="0"
                    value={formData.notice_period_days}
                    onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                    placeholder="Bijv. 30"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cost_amount">Kosten</Label>
                  <Input
                    id="cost_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost_amount}
                    onChange={(e) => setFormData({ ...formData, cost_amount: e.target.value })}
                    placeholder="0,00"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Betaalfrequentie</Label>
                  <Select value={formData.cost_frequency} onValueChange={(v) => setFormData({ ...formData, cost_frequency: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((freq) => (
                        <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Verantwoordelijke</Label>
                  <Select value={formData.responsible_user_id || 'none'} onValueChange={(v) => setFormData({ ...formData, responsible_user_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen verantwoordelijke" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen verantwoordelijke</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Afdeling / module</Label>
                  <Select value={formData.department || 'none'} onValueChange={(v) => setFormData({ ...formData, department: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen afdeling" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen afdeling</SelectItem>
                      {DEPARTMENTS.map((dept) => (
                        <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.auto_renewal}
                  onChange={(e) => setFormData({ ...formData, auto_renewal: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Contract wordt automatisch verlengd
              </label>

              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Interne notities"
                className="rounded-xl resize-none"
                rows={2}
              />

              {editingContract && (
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label>Documenten</Label>
                    <label className="cursor-pointer">
                      <span className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800">
                        <Upload className="w-4 h-4" />
                        {uploading ? 'Bezig...' : 'Bestand uploaden'}
                      </span>
                      <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                    </label>
                  </div>
                  {documents.length === 0 ? (
                    <p className="text-sm text-slate-400">Nog geen documenten geüpload.</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-2 text-sm">
                          <span className="truncate">{doc.original_filename}</span>
                          <div className="flex items-center gap-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc)}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingContract ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Meldingen instellen</DialogTitle>
            </DialogHeader>
            {settingsForm && (
              <form onSubmit={handleSaveSettings} className="space-y-4">
                <div className="space-y-2">
                  <Label>Dagen vóór einddatum (komma-gescheiden)</Label>
                  <Input
                    value={settingsForm.days_before_end}
                    onChange={(e) => setSettingsForm({ ...settingsForm, days_before_end: e.target.value })}
                    placeholder="30, 14, 7"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dagen vóór opzegdeadline</Label>
                  <Input
                    value={settingsForm.days_before_notice_deadline}
                    onChange={(e) => setSettingsForm({ ...settingsForm, days_before_notice_deadline: e.target.value })}
                    placeholder="30, 14"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dagen vóór automatische verlenging</Label>
                  <Input
                    value={settingsForm.days_before_auto_renewal}
                    onChange={(e) => setSettingsForm({ ...settingsForm, days_before_auto_renewal: e.target.value })}
                    placeholder="30"
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_on_expired}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_on_expired: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Melden bij verlopen contract
                  </label>
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_on_missing_responsible}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_on_missing_responsible: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Melden zonder verantwoordelijke
                  </label>
                </div>

                <div className="space-y-2">
                  <Label>Meldingen gaan naar (rollen)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['admin', 'teamleader', 'hoofd_facilitair', 'leiderschap'].map((role) => (
                      <label key={role} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={settingsForm.recipient_roles.includes(role)}
                          onChange={() => toggleRecipientRole(role)}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        {role.replace('_', ' ')}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Meldingen ook naar specifieke personen</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {teamMembers.map((member) => (
                      <label key={member.id} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={settingsForm.recipient_user_ids.includes(member.id)}
                          onChange={() => toggleRecipientUser(member.id)}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        {member.name}
                      </label>
                    ))}
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-xl">
                    Annuleren
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                    Opslaan
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ContractManagementPage;
